import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Case } from '../../types';
import { ArrowRightIcon, ClockIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { getDraggableCaseStatusClass } from '../../utils/statusUtils';
import { getChassisNumberLast4Digits } from '../../utils/vehicleUtils';
import { ContextMenu, ContextMenuItem } from '../ui/ContextMenu';

interface DraggableCaseProps {
  case: Case;
  onClick?: () => void;
  onUnassign?: (caseId: string) => void;
  timeSlotHeight?: number;
  duration?: number;
  getVehicleName?: (case_: Case) => string;
  getClientName?: (clientId: string) => string;
  getBillingPartnerName?: (case_: Case) => string;
  getPickupPartnerName?: (case_: Case) => string;
  getDeliveryPartnerName?: (case_: Case) => string;
  // グリッド表示用の位置情報
  gridPosition?: {
    left: number;
    width: number;
    top: number;
    height?: number;
  };
}

export const DraggableCase: React.FC<DraggableCaseProps> = ({
  case: caseData,
  onClick,
  onUnassign,
  timeSlotHeight: _timeSlotHeight,
  duration: _duration,
  getVehicleName,
  getBillingPartnerName,
  getPickupPartnerName,
  getDeliveryPartnerName,
  gridPosition,
}) => {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: caseData.id,
    data: {
      type: 'case',
      case: caseData,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    ...(gridPosition
      ? {
          position: 'absolute' as const,
          height: `${gridPosition.height}px`,
          left: `${gridPosition.left}px`,
          width: `${gridPosition.width}px`,
          top: `${gridPosition.top}px`,
          zIndex: 10,
        }
      : {
          height: 'auto',
          width: '100%',
        }),
  };

  const handleCaseClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) return;

    e.stopPropagation();
    e.preventDefault();

    if (onClick) {
      onClick();
    } else {
      navigate(`/cases/${caseData.id}`);
    }
  };

  const handleUnassign = () => {
    if (onUnassign && confirm('この案件を未割り当てに戻しますか？')) {
      onUnassign(caseData.id);
    }
  };

  const contextMenuItems: ContextMenuItem[] = [
    {
      id: 'view-details',
      label: '案件詳細を見る',
      onClick: () => navigate(`/cases/${caseData.id}`),
      className: 'text-gray-700 dark:text-gray-200',
    },
    ...(caseData.assignedLandTransporterId && onUnassign
      ? [
          {
            id: 'unassign',
            label: '未割り当てに戻す',
            icon: XMarkIcon,
            onClick: handleUnassign,
            className: 'text-red-600 dark:text-red-400',
          },
        ]
      : []),
  ];

  const billingPartnerName = getBillingPartnerName ? getBillingPartnerName(caseData) : '請求先不明';
  const pickupPartnerName = getPickupPartnerName ? getPickupPartnerName(caseData) : '引取先不明';
  const deliveryPartnerName = getDeliveryPartnerName
    ? getDeliveryPartnerName(caseData)
    : '搬入先不明';
  const vehicleName = getVehicleName ? getVehicleName(caseData) : '車両名不明';

  const isAssigned = !!caseData.assignedLandTransporterId;

  // 受付状態の判定（仮受・本受）
  // acceptanceStatusフィールドを優先的に使用（従来システム互換性）
  // フィールドがない場合はstatusから判定（後方互換性）
  const acceptanceStatus: 'provisional' | 'confirmed' | undefined =
    caseData.acceptanceStatus ||
    (caseData.status === 'provisional'
      ? 'provisional'
      : caseData.status === 'confirmed'
        ? 'confirmed'
        : undefined);

  return (
    <ContextMenu items={contextMenuItems}>
      <div
        ref={setNodeRef}
        style={style}
        {...listeners}
        {...attributes}
        className={`
          ${gridPosition ? 'absolute' : 'relative'} border rounded p-1.5 cursor-grab active:cursor-grabbing
          transition-all duration-200 hover:shadow-md text-xs
          ${getDraggableCaseStatusClass(caseData.status, caseData.assignedLandTransporterId, acceptanceStatus)}
          ${isDragging ? 'opacity-50 scale-105 shadow-lg' : ''}
        `}
        onClick={handleCaseClick}
      >
        <div className='flex flex-col justify-center h-full'>
          <div className='flex items-center justify-between mb-1'>
            <div className='flex items-center flex-1 min-w-0'>
              {caseData.caseNumber && (
                <span className='text-xs text-blue-600 dark:text-blue-400 font-medium mr-2 flex-shrink-0'>
                  {caseData.caseNumber}
                </span>
              )}
              <span className='text-xs font-medium text-gray-700 dark:text-gray-300 mr-2 flex-shrink-0'>
                {vehicleName}
                {caseData.chassisNumber && (
                  <span className='text-[10px] font-mono text-gray-600 dark:text-gray-400'>
                    ({getChassisNumberLast4Digits(caseData.chassisNumber)})
                  </span>
                )}
              </span>
              <span className='font-medium text-xs truncate text-gray-900 dark:text-white flex-1 min-w-0'>
                {billingPartnerName}
              </span>
              {/* 受付状態ラベル（仮受・本受） */}
              {acceptanceStatus === 'provisional' && (
                <span className='px-1.5 py-0.5 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 text-[10px] font-semibold rounded ml-2 flex-shrink-0'>
                  📋 仮受
                </span>
              )}
              {acceptanceStatus === 'confirmed' && (
                <span className='px-1.5 py-0.5 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 text-[10px] font-semibold rounded ml-2 flex-shrink-0 flex items-center gap-0.5'>
                  <CheckCircleIcon className='w-3 h-3' />✓ 本受
                </span>
              )}
            </div>
          </div>

          <div className='flex items-center justify-between text-xs text-gray-600 dark:text-gray-400'>
            <div className='flex items-center flex-1 min-w-0'>
              <MapPinIcon className='w-3 h-3 text-gray-500 mr-1 flex-shrink-0' />
              <span className='truncate'>{pickupPartnerName}</span>
              <ArrowRightIcon className='w-2.5 h-2.5 mx-1' />
              <span className='truncate'>{deliveryPartnerName}</span>
            </div>

            {!isAssigned && (
              <div className='flex items-center ml-2 flex-shrink-0'>
                <ClockIcon className='w-2.5 h-2.5 mr-1' />
                <span className='font-medium text-[10px]'>{caseData.pickupTimeStart}</span>
                <ArrowRightIcon className='w-2.5 h-2.5 mx-0.5' />
                <span className='font-medium text-[10px]'>{caseData.deliveryTime}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </ContextMenu>
  );
};
